import React, { createContext, useContext, useMemo, useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import { useTheme, useMediaQuery } from '@mui/material'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import {
  setRowSelectionModel,
  setDrawerWidth,
  setIsFilterPanelOpen,
  setFilterPanelSizes,
  selectCSDDrawerWidth,
  selectCSDIsFilterPanelOpen,
  selectCSDFilterPanelSizes,
  FILTER_PANEL_COLLAPSE_WIDTH,
  FILTER_PANEL_DEFAULT_OPEN_WIDTH,
} from 'slices/configuredService/createDraftCSDSlice'

import { RECURRENCE_V2 } from 'settings/constants/service'
import { useGetTagsMetadataQuery } from 'api/tags/getTagsMetadata'
import { getServiceTags } from 'data/tags/tagsMetadataSelector'
import { useCreateDraftCSDInit } from 'components/configured-service/CreateDraftConfiguredServiceDialog/hooks/useCreateDraftCSDInit'
import { useCreateDraftServiceFormInit } from 'components/configured-service/CreateDraftConfiguredServiceDialog/hooks/useCreateDraftServiceFormInit'
import { useCreateDraftServiceHandlers } from 'components/configured-service/CreateDraftConfiguredServiceDialog/hooks/useCreateDraftServiceHandlers'
import { canUpdateConfiguredServicePrice } from 'data/permissions/permissionsSelectors'
import { defaultValues } from 'components/configured-service/CreateDraftConfiguredServiceDialog/settings'
import { filterDefaultValues } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filterSettings'

const FILTER_PANEL_MIN_WIDTH_PX = 240

const CreateDraftConfiguredServiceContext = createContext(null)

export const useCreateDraftConfiguredServiceContext = () => {
  const context = useContext(CreateDraftConfiguredServiceContext)
  if (!context) {
    throw new Error('useCreateDraftConfiguredServiceContext must be used within a CreateDraftConfiguredServiceProvider')
  }
  return context
}

export const CreateDraftConfiguredServiceProvider = ({ children, isOpen, accountId, locationId, onClose, onSuccess, onSuccessReturnToAccount, renderAsPanel = false }) => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const dispatch = useDispatch()

  const canEditPrice = useSelector(canUpdateConfiguredServicePrice, shallowEqual)
  const drawerWidth = useSelector(selectCSDDrawerWidth, shallowEqual)
  const isFilterPanelOpen = useSelector(selectCSDIsFilterPanelOpen, shallowEqual)
  const filterPanelSizes = useSelector(selectCSDFilterPanelSizes, shallowEqual)

  useGetTagsMetadataQuery()
  const serviceTags = useSelector(getServiceTags)

  const methods = useForm({
    defaultValues: {
      ...defaultValues,
      ...filterDefaultValues,
      location: null,
      serviceStartDate: new Date(),
      onRequestAction: null,
      forPublish: false,
      selectedPricedService: null,
      tags: [],
      autoCalculatePrice: true,
    },
    shouldUnregister: false,
    mode: 'onChange',
  })

  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors, dirtyFields },
  } = methods

  const location = watch('location')

  const selectedLocationId = location?.id || location?.value || locationId

  const selectedPricedService = watch('selectedPricedService')
  const isSelectedPricedServiceOnRequest = selectedPricedService?.serviceType === RECURRENCE_V2.ON_REQUEST

  const { metaFiltersData, recurrenceOptions, handleRegisterFiltersReset, resetDialogState } = useCreateDraftCSDInit({
    isOpen,
    accountId,
    locationId: selectedLocationId,
    isDesktop,
    methods,
    selectedPricedService,
  })

  const { locations, actionList, pricingServices, slotPropsPopper } = useCreateDraftServiceFormInit({
    isOpen,
    getValues,
    setValue,
    accountId,
    selectedLocationId,
    selectedPricedService,
  })

  const { onFilterChange, getLocationOptionLabel, handleLocationAutocompleteChange, isLocationOptionEqualToValue } =
    useCreateDraftServiceHandlers()

  const handleClearSelection = useCallback(() => {
    setValue('selectedPricedService', null)
    dispatch(setRowSelectionModel([]))
  }, [dispatch, setValue])

  const handleToggleFilterPanel = useCallback(() => {
    const opening = !isFilterPanelOpen
    if (opening) {
      const targetWidth = Math.max(drawerWidth, FILTER_PANEL_DEFAULT_OPEN_WIDTH)
      if (drawerWidth < FILTER_PANEL_DEFAULT_OPEN_WIDTH) {
        dispatch(setDrawerWidth(FILTER_PANEL_DEFAULT_OPEN_WIDTH))
      }
      const minPercent = Math.ceil((FILTER_PANEL_MIN_WIDTH_PX / targetWidth) * 100)
      if (filterPanelSizes.filterPanel < minPercent) {
        dispatch(setFilterPanelSizes({ filterPanel: minPercent, servicesPanel: 100 - minPercent }))
      }
    }
    dispatch(setIsFilterPanelOpen(opening))
  }, [dispatch, isFilterPanelOpen, drawerWidth, filterPanelSizes])

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)

  const handleOpenMobileFilterPanel = useCallback(() => setIsMobileFilterOpen(true), [])

  const handleCloseMobileFilterPanel = useCallback(() => setIsMobileFilterOpen(false), [])

  useEffect(() => {
    if (!isOpen) {
      setIsMobileFilterOpen(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (renderAsPanel) {
      setIsMobileFilterOpen(false)
    }
  }, [renderAsPanel])

  const handleFilterPanelLayoutChange = useCallback(
    sizes => {
      if (isFilterPanelOpen && sizes?.length === 2) {
        dispatch(setFilterPanelSizes({ filterPanel: sizes[0], servicesPanel: sizes[1] }))
      }
    },
    [dispatch, isFilterPanelOpen]
  )

  useEffect(() => {
    if (renderAsPanel && drawerWidth < FILTER_PANEL_COLLAPSE_WIDTH && isFilterPanelOpen) {
      dispatch(setIsFilterPanelOpen(false))
    }
  }, [renderAsPanel, drawerWidth, isFilterPanelOpen, dispatch])

  const contextValue = useMemo(
    () => ({
      isDesktop,
      isOpen,
      onClose,
      onSuccess,
      onSuccessReturnToAccount,

      control,
      getValues,
      setValue,
      watch,
      errors,
      methods,
      dirtyFields,

      accountId,
      locations,
      actionList,
      pricingServices,
      metaFiltersData,
      recurrenceOptions,
      selectedPricedService,
      isSelectedPricedServiceOnRequest,
      canEditPrice,
      slotPropsPopper,
      serviceTags,
      getLocationOptionLabel,
      handleLocationAutocompleteChange,
      onFilterChange,
      handleRegisterFiltersReset,
      resetDialogState,
      handleClearSelection,
      isLocationOptionEqualToValue,
      drawerWidth,
      isFilterPanelOpen,
      filterPanelSizes,
      handleToggleFilterPanel,
      handleFilterPanelLayoutChange,
      renderAsPanel,
      isMobileFilterOpen,
      handleOpenMobileFilterPanel,
      handleCloseMobileFilterPanel,
    }),
    [
      isDesktop,
      isOpen,
      onClose,
      onSuccess,
      onSuccessReturnToAccount,
      control,
      getValues,
      setValue,
      watch,
      errors,
      methods,
      dirtyFields,
      accountId,
      locations,
      actionList,
      pricingServices,
      metaFiltersData,
      recurrenceOptions,
      selectedPricedService,
      isSelectedPricedServiceOnRequest,
      canEditPrice,
      slotPropsPopper,
      serviceTags,
      getLocationOptionLabel,
      handleLocationAutocompleteChange,
      onFilterChange,
      handleRegisterFiltersReset,
      resetDialogState,
      handleClearSelection,
      isLocationOptionEqualToValue,
      drawerWidth,
      isFilterPanelOpen,
      filterPanelSizes,
      handleToggleFilterPanel,
      handleFilterPanelLayoutChange,
      renderAsPanel,
      isMobileFilterOpen,
      handleOpenMobileFilterPanel,
      handleCloseMobileFilterPanel,
    ]
  )

  return (
    <CreateDraftConfiguredServiceContext.Provider value={contextValue}>
      <FormProvider {...methods}>{children}</FormProvider>
    </CreateDraftConfiguredServiceContext.Provider>
  )
}

CreateDraftConfiguredServiceProvider.propTypes = {
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  onSuccessReturnToAccount: PropTypes.func,
  renderAsPanel: PropTypes.bool,
}
